import { Grid } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Accordion, Button, Col, Row } from "react-bootstrap";
import DatePicker from "react-date-picker";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx/dist/xlsx.full.min.js";
import { vasthuUrl } from "../../component/Api";
import NewBootCard from "../../component/NewBootCard";

function AdminAudioCallHistory() {
  const token = window.sessionStorage.getItem("token");
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clientData, setClientData] = useState([]);
  const [searchField, setSearchField] = useState("");

  const [openIndex, setOpenIndex] = useState(null); // Lazy open optimization

  const [totalAmount, setTotalAmount] = useState("");
  const [totalDuration, setTotalDuration] = useState("");
  const [totalSalary, setTotalSalary] = useState("");

  const today = new Date();

  // ------------------ API CALL ------------------
  useEffect(() => {
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

      vasthuUrl
        .get("vasthu/user/getallcallHistoryofconsultantBydate", {
          params: {
            fromDate: formattedStartDate,
            toDate: formattedEndDate,
          },
        })
        .then((res) => {
          handleAPIResponse(res.data);
        })
        .catch((error) => console.error("Error fetching data:", error));
    } else {
      vasthuUrl
        .get(`vasthu/user/getallcallhistoryofconsultant`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          handleAPIResponse(res.data);
        })
        .catch((e) => console.log("Error:", e));
    }
  }, [startDate, endDate]);

  // ------------------ Handle API Data ------------------
  const handleAPIResponse = (data) => {
    if (data?.message) {
      setClientData([]);
      setTotalAmount(0);
      setTotalDuration(0);
      setTotalSalary(0);
      return;
    }

    const dataWithId = data.map((item, index) => ({
      ...item,
      id: item.orderId || index,
    }));

    setClientData(dataWithId);

    // initial totals
    setTotalAmount(data.reduce((sum, i) => sum + i.totalAmountCharged, 0));
    setTotalDuration(data.reduce((sum, i) => sum + i.totalDuration, 0));
    setTotalSalary(data.reduce((sum, i) => sum + i.totalSalaryAmount, 0));
  };

  // ------------------ Memoized Filter ------------------
  const ConsultantCallList = useMemo(() => {
    return clientData.filter((person) =>
      person.callFromId?.toLowerCase().includes(searchField.toLowerCase())
    );
  }, [clientData, searchField]);

  // ------------------ Memoized Totals ------------------
  const totalDurationMemo = useMemo(
    () => ConsultantCallList.reduce((sum, i) => sum + i.totalDuration, 0),
    [ConsultantCallList]
  );

  const totalAmountMemo = useMemo(
    () => ConsultantCallList.reduce((sum, i) => sum + i.totalAmountCharged, 0),
    [ConsultantCallList]
  );

  const totalSalaryMemo = useMemo(
    () => ConsultantCallList.reduce((sum, i) => sum + i.totalSalaryAmount, 0),
    [ConsultantCallList]
  );

  // Set updated totals
  useEffect(() => {
    setTotalDuration(totalDurationMemo);
    setTotalAmount(totalAmountMemo);
    setTotalSalary(totalSalaryMemo);
  }, [totalDurationMemo, totalAmountMemo, totalSalaryMemo]);

  // ------------------ Columns ------------------
  const columns = [
    { field: "call_to", headerName: "User", width: 130 },
    { field: "duration", headerName: "Duration", width: 100 },
    {
      field: "start_time",
      headerName: "Date",
      renderCell: (params) =>
        dayjs(params.row.start_time).format("DD-MM-YYYY HH:mm"),
      width: 150,
    },
    {
      field: "proposed_call_duration",
      headerName: "Proposed Duration",
      width: 130,
    },
    { field: "amount_charged", headerName: "Amount Charged", width: 130 },
    { field: "amountToPaid", headerName: "Earnings", width: 130 },
    { field: "con_rate", headerName: "Consultant Rate", width: 130 },
    { field: "call_type", headerName: "Call Type", width: 130 },
    { field: "status", headerName: "Status", width: 130 },
  ];

  // ------------------ Excel ------------------
  const downloadExcel = async () => {
    let serialNumber = 1;

    const formattedData = ConsultantCallList.flatMap((consultant) =>
      consultant.AllCallDetails.map((call) => ({
        "S.No": serialNumber++,
        "Consultant Name": consultant.callFromId,
        "Customer Name": call.call_to,
        Duration: call.duration,
        Date: dayjs(call.start_time).format("DD-MM-YYYY"),
        "Amount Charged": call.amount_charged,
        "Consultant Rate": call.con_rate,
        Status: call.status,
        "Call Type": call.call_type,
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "CallSheet.xlsx");
  };

  return (
    <div>
      {/* ------------------ Search + Date Filter ------------------ */}
      <Row className="mt-2 consul_chat_his" style={{ alignItems: "flex-end" }}>
        <Col sm={12} md={4} className="mt-2">
          <input
            type="text"
            className="form-control form_feild"
            placeholder={t("CM.CNSL.SER.PL")}
            onChange={(e) => setSearchField(e.target.value)}
          />
        </Col>

        <Col sm={12} md={4} className="mt-2">
          <label>
            Start Date:
            <DatePicker
              className="form_feild"
              value={startDate}
              format="dd-MM-yy"
              onChange={(date) => setStartDate(date)}
              maxDate={endDate ? endDate : today}
            />
          </label>
        </Col>

        <Col sm={12} md={4} className="mt-2">
          <label>
            End Date:
            <DatePicker
              className="form_feild"
              value={endDate}
              format="dd-MM-yy"
              onChange={(date) => setEndDate(date)}
              minDate={startDate}
              maxDate={today}
            />
          </label>
        </Col>
      </Row>

      {/* ------------------ Summary ------------------ */}
      {ConsultantCallList.length ? (
        <div>
          <center>
            <h3 className="mt-2">
              {startDate && endDate
                ? `${dayjs(startDate).format("DD-MM-YYYY")} to ${dayjs(
                    endDate
                  ).format("DD-MM-YYYY")} Consultant Call History`
                : "OverAll Consultant Call History"}
            </h3>
          </center>

          <div className="d-flex justify-content-around">
            <h3>Earnings: {totalAmount}</h3>
            <h3>Durations: {totalDuration}</h3>
            <h3>Salary: {totalSalary}</h3>
          </div>

          <div style={{ textAlign: "end", marginRight: "10px" }}>
            <Button className="all_btn1" onClick={downloadExcel}>
              Download Excel
            </Button>
          </div>

          {/* ------------------ Consultant Accordions ------------------ */}
          {ConsultantCallList.map((i, j) => (
            <Accordion className="chat_accordian mt-2" key={j}>
              <Accordion.Item
                className="accordian_style"
                eventKey={j.toString()}
              >
                <Accordion.Header onClick={() => setOpenIndex(j)}>
                  <Row style={{ width: "inherit" }}>
                    <Col sm={12} md={3}>
                      <h5>Consultant: {i.callFromId}</h5>
                    </Col>
                    <Col sm={12} md={3}>
                      <h5>Duration: {i.totalDuration}</h5>
                    </Col>
                    <Col sm={12} md={3}>
                      <h5>Amount: ₹{i.totalAmountCharged}</h5>
                    </Col>
                    <Col sm={12} md={3}>
                      <h5>Salary: ₹{i.totalSalaryAmount}</h5>
                    </Col>
                  </Row>
                </Accordion.Header>

                <Accordion.Body>
                  {/* Lazy render DataGrid ONLY when accordion is opened */}
                  {openIndex === j && (
                    <>
                      <center className="datagrid mt-3">
                        <Grid container className="deskTable datagrid">
                          <DataGrid
                            className="all_border text-center me-3 data_grid"
                            rows={i.AllCallDetails}
                            columns={columns}
                            initialState={{
                              pagination: {
                                paginationModel: { page: 0, pageSize: 10 },
                              },
                            }}
                            pageSizeOptions={[5, 10]}
                            getRowId={(row) => row.id}
                          />
                        </Grid>
                      </center>

                      <div className="mobileCard mx-1">
                        {i.AllCallDetails.map((item, index) => (
                          <NewBootCard key={index}>
                            <h6>User: {item.call_to}</h6>
                            <h6>Duration: {item.duration}</h6>
                            <h6>
                              Date:{" "}
                              {dayjs(item.start_time).format(
                                "DD-MM-YYYY HH:mm"
                              )}
                            </h6>
                            <h6>
                              Proposed Duration: {item.proposed_call_duration}
                            </h6>
                            <h6>Amount Charged: {item.amount_charged}</h6>
                            <h6>Earnings: {item.amountToPaid}</h6>
                            <h6>Consultant Rate: {item.con_rate}</h6>
                            <h6>Call Type: {item.call_type}</h6>
                            <h6>Status: {item.status}</h6>
                          </NewBootCard>
                        ))}
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          ))}
        </div>
      ) : (
        <center>
          <h2 className="mt-2">No Record Found!</h2>
        </center>
      )}
    </div>
  );
}

export default React.memo(AdminAudioCallHistory);
